import React, { useState, useEffect } from 'react';
import { Check, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback data in case API is unavailable (moved outside component to avoid ESLint warning)
const fallbackPricingData = [
  {
    id: 1,
    name: "Initial Assessment",
    duration: "45 minutes",
    price: "£70",
    description: "Comprehensive evaluation including detailed history, physical examination, diagnosis, and personalized treatment plan.",
    features: [
      "Full injury/condition assessment",
      "Movement and postural analysis",
      "Diagnosis and treatment plan",
      "Initial hands-on treatment",
      "Home exercise program"
    ],
    popular: false
  },
  {
    id: 2,
    name: "Follow-up Treatment",
    duration: "30 minutes",
    price: "£60",
    description: "Continuation of your rehabilitation program with hands-on treatment and exercise progression.",
    features: [
      "Manual therapy techniques",
      "Exercise rehabilitation",
      "Progress assessment",
      "Treatment plan updates",
      "Ongoing support and advice"
    ],
    popular: true
  },
  {
    id: 3,
    name: "Sports Massage",
    duration: "30 minutes",
    price: "£60",
    description: "Therapeutic massage for injury recovery, performance enhancement, and maintenance.",
    features: [
      "Deep tissue massage",
      "Trigger point therapy",
      "Soft tissue mobilization",
      "Taping (if required)",
      "Self-care advice"
    ],
    popular: false
  },
  {
    id: 4,
    name: "Home Visit Session",
    duration: "60 minutes",
    price: "£100",
    description: "Comprehensive physiotherapy treatment in the comfort of your own home. Perfect for those with mobility issues or prefer home-based care.",
    features: [
      "Treatment at your location",
      "Full assessment or follow-up",
      "All equipment provided",
      "Personalized care plan",
      "Travel included within Harrow"
    ],
    popular: false
  }
];

const fallbackPackageDeals = [
  {
    name: "5-Session Treatment Package",
    sessions: "5 x 30-minute Follow-up Sessions",
    price: "£270",
    savings: "Save £30",
    description: "Pre-pay for 5 sessions and save money on your treatment course"
  },
  {
    name: "10-Session Treatment Package",
    sessions: "10 x 30-minute Follow-up Sessions",
    price: "£500",
    savings: "Save £100",
    description: "Ideal for ongoing treatment and injury prevention"
  }
];

const Pricing = () => {
  const [pricingData, setPricingData] = useState([]);
  const [packageDeals, setPackageDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch pricing and packages from API
  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        const [pricingResponse, packagesResponse] = await Promise.all([
          fetch(`${API_URL}/api/pricing`),
          fetch(`${API_URL}/api/packages`)
        ]);

        if (pricingResponse.ok && packagesResponse.ok) {
          const pricing = await pricingResponse.json();
          const packages = await packagesResponse.json();
          setPricingData(pricing);
          setPackageDeals(packages);
        } else {
          // Fallback to static data
          setPricingData(fallbackPricingData);
          setPackageDeals(fallbackPackageDeals);
        }
      } catch (error) {
        console.error('Error fetching pricing data:', error);
        // Fallback to static data
        setPricingData(fallbackPricingData);
        setPackageDeals(fallbackPackageDeals);
      } finally {
        setLoading(false);
      }
    };

    fetchPricingData();
  }, []);

  const scrollToContact = () => {
    window.location.href = '/contact';
  };


  return (
    <div className="min-h-screen bg-white pt-20">
      <SEO 
        title="Pricing - Physiotherapy Services in Harrow | MSK and Sports Physio"
        description="Transparent pricing for physiotherapy in Harrow. Initial assessment £70, follow-up treatments £60. Package deals available. Insurance accepted: AXA, Bupa, Aviva, Vitality, WPA."
        keywords="physiotherapy prices Harrow, sports physio cost, MSK treatment pricing, physiotherapy insurance Harrow, AXA physiotherapy, Bupa physiotherapy"
        type="website"
      />
      {/* Hero Section */}
      <section className="py-8 px-4 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto max-w-4xl text-center">
          <Badge className="bg-[#dbeafe] text-[#2563eb] hover:bg-[#dbeafe] mb-4">Pricing</Badge>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">

          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">

          </p>
        </div>
      </section>

      {/* Individual Sessions */}
      <section className="py-12 px-4 lg:px-8">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Individual Sessions
            </h2>
            <p className="text-lg text-gray-600">
              Choose the session type that best suits your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {loading ? (
              // Loading skeleton
              Array.from({ length: 4 }).map((_, idx) => (
                <Card key={idx} className="border-2 border-gray-200 animate-pulse">
                  <CardHeader className="pb-4">
                    <div className="h-6 bg-gray-200 rounded w-2/3 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-10 bg-gray-200 rounded w-1/3 mt-4"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 mb-6">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="h-4 bg-gray-200 rounded"></div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              pricingData.map((item) => (
                <Card
                  key={item.id}
                  className={`relative border-2 hover:shadow-xl transition-all duration-300 ${
                    item.popular ? 'border-[#2563eb]' : 'border-gray-100'
                  }`}
                >
                  {item.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-[#2563eb] text-white px-4 py-1">Most Popular</Badge>
                    </div>
                  )}
                  <CardHeader className="pb-4">
                    <CardTitle className="text-2xl text-gray-900">{item.name}</CardTitle>
                    <CardDescription className="text-base pt-2">
                      {item.description}
                    </CardDescription>
                    <div className="flex items-baseline gap-2 pt-4">
                      <span className="text-4xl font-bold text-[#2563eb]">{item.price}</span>
                      <span className="text-gray-600">/ {item.duration}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-6">
                      {item.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-[#2563eb] flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      onClick={scrollToContact}
                      className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white h-12"
                    >
                      Book This Session
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Package Deals */}
      <section className="py-20 px-4 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Package Deals
            </h2>
            <p className="text-lg text-gray-600">
              Save money with our pre-paid treatment packages
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {loading ? (
              // Loading skeleton for packages
              Array.from({ length: 2 }).map((_, idx) => (
                <Card key={idx} className="border-2 border-gray-200 animate-pulse">
                  <CardHeader>
                    <div className="h-6 bg-gray-200 rounded w-2/3 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-10 bg-gray-200 rounded w-1/3 mb-6"></div>
                    <div className="h-12 bg-gray-200 rounded"></div>
                  </CardContent>
                </Card>
              ))
            ) : (
              packageDeals.map((pkg, index) => (
                <Card key={index} className="border-2 border-[#dbeafe] hover:border-[#06b6d4] hover:shadow-xl transition-all duration-300">
                  <CardHeader>
                    <div className="flex justify-between items-start mb-2">
                      <CardTitle className="text-2xl text-gray-900">{pkg.name}</CardTitle>
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                        {pkg.savings}
                      </Badge>
                    </div>
                    <CardDescription className="text-base pt-2">
                      {pkg.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-6">
                      <div className="text-sm text-gray-600 mb-2">{pkg.sessions}</div>
                      <div className="text-4xl font-bold text-[#2563eb]">{pkg.price}</div>
                    </div>
                    <Button
                      onClick={scrollToContact}
                      variant="outline"
                      className="w-full border-2 border-[#2563eb] text-[#2563eb] hover:bg-[#eff6ff] h-12"
                    >
                      Enquire About Package
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Insurance Information */}
      <section className="py-20 px-4 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          <Card className="border-2 border-[#dbeafe]">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl text-gray-900 mb-4">Insurance Coverage</CardTitle>
              <CardDescription className="text-base">
                We work with major health insurance providers
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Accepted Insurance Providers:</h3>
                  <ul className="space-y-2">
                    {['AXA Health', 'Bupa', 'Aviva', 'Vitality', 'WPA'].map((provider, idx) =>
                    <li key={idx} className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-[#2563eb]" />
                        <span className="text-gray-700">{provider}</span>
                      </li>
                    )}
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">What You Need to Know:</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Check your policy covers outpatient physiotherapy</li>
                    <li>• Bring your insurance details to your first session</li>
                    <li>• We provide detailed receipts for claims</li>
                    <li>• Some policies require GP referral</li>
                    <li>• Contact your insurer to confirm coverage</li>
                  </ul>
                </div>
              </div>
              <div className="bg-[#eff6ff] rounded-lg p-6 mt-6">
                <p className="text-gray-700 text-center">
                  <strong>Insurance claims:</strong> Please ensure you have your authorization from your insurance company before attending your session.
                
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Cancellation Policy */}
      <section className="py-20 px-4 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <Card className="border-2 border-gray-200">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">Cancellation Policy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-700">
              <p>
                We understand that sometimes appointments need to be changed. To ensure we can accommodate all patients:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="flex items-start gap-3">
                  <span className="font-semibold text-[#2563eb] mt-1">•</span>
                  <span><strong>48-hour notice required:</strong> Please provide at least 48 hours notice if you need to cancel or reschedule your appointment.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-semibold text-[#2563eb] mt-1">•</span>
                  <span><strong>Late cancellations:</strong> Cancellations with less than 48 hours notice will be charged at 100% of the session fee.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-semibold text-[#2563eb] mt-1">•</span>
                  <span><strong>Missed appointments:</strong> Failure to attend without prior notice will be charged at the full session rate.</span>
                </li>
              </ul>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6">
                <p className="text-sm">
                  <strong>To cancel or reschedule:</strong> Please call <a href="tel:07747075809" className="text-[#2563eb] hover:underline">07747075809</a> or 
                  email <a href="mailto:mskandsportsphysio@gmail.com" className="text-[#2563eb] hover:underline">mskandsportsphysio@gmail.com</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 lg:px-8 bg-gradient-to-br from-[#2563eb] to-[#06b6d4] text-white">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Start Your Recovery?
          </h2>
          <p className="text-xl mb-8 text-blue-50">
            Book your initial assessment today and take the first step towards regaining strength and restoring movement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={scrollToContact}
              size="lg"
              className="bg-white text-[#2563eb] hover:bg-gray-100 px-8 py-6 text-lg rounded-xl">

              Book Appointment
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl"
              onClick={() => window.location.href = 'tel:07747075809'}>

              <Phone className="mr-2 w-5 h-5" />
              Call Now
            </Button>
          </div>
        </div>
      </section>
    </div>);

};

export default Pricing;